import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Image,
  FileSpreadsheet,
  File,
  Download,
  UserCheck,
  Building2,
  Stethoscope,
  Calendar,
  Clock,
} from 'lucide-react';
import type { Attachment } from '@refdash/api-client';
import { api } from '@/lib/api';
import { formatDate, outcomeLabel } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PatientInfoCard } from '@/components/patient-info-card';
import { CaseTimeline } from '@/components/case-timeline';
import { DOC_TYPE_LABELS } from '@/lib/case-labels';
import { cn } from '@/lib/utils';

function getFileIcon(mime: string) {
  if (mime.startsWith('image/')) return Image;
  if (mime.includes('pdf')) return FileText;
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv'))
    return FileSpreadsheet;
  return File;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function OutcomeBadge({
  outcome,
  decision,
  submittedAt,
}: {
  outcome?: string | null;
  decision?: string | null;
  submittedAt?: string | null;
}) {
  if (outcome === 'confirmed') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
        <CheckCircle className="w-3.5 h-3.5" />
        {outcomeLabel(outcome, submittedAt)}
      </span>
    );
  }
  if (outcome === 'unsuccessful') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">
        <AlertCircle className="w-3.5 h-3.5" />
        {outcomeLabel(outcome, submittedAt)}
      </span>
    );
  }
  if (outcome === 'missed') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-stone-100 text-stone-500">
        <Clock className="w-3.5 h-3.5" />
        {outcomeLabel(outcome, submittedAt)}
      </span>
    );
  }
  if (outcome === 'error') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700">
        <XCircle className="w-3.5 h-3.5" />
        {outcomeLabel(outcome, submittedAt)}
      </span>
    );
  }
  // Fall back to decision
  if (decision === 'accept') {
    return <Badge variant="success">Accepted</Badge>;
  }
  if (decision === 'reject') {
    return <Badge variant="destructive">Rejected</Badge>;
  }
  return <Badge variant="secondary">Pending</Badge>;
}

export function CaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from') || '/history';

  const approvalId = Number(id);

  const { data: detail, isLoading } = useQuery({
    queryKey: ['approvals', 'detail', approvalId],
    queryFn: () => api.approvals.getDetail(approvalId),
    enabled: !!approvalId,
  });

  if (isLoading) {
    return (
      <div className="px-5 py-5 lg:px-8 lg:py-8 max-w-lg lg:max-w-3xl mx-auto space-y-4">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-48 rounded-2xl" />
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-24 rounded-2xl" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="px-5 py-20 text-center max-w-lg lg:max-w-3xl mx-auto">
        <p className="text-muted-foreground">Case not found.</p>
        <Button
          variant="ghost"
          className="mt-4 gap-2"
          onClick={() => navigate(from)}
        >
          <ArrowLeft className="w-4 h-4" />
          Go back
        </Button>
      </div>
    );
  }

  const attachments = detail.attachments ?? [];

  return (
    <div className="px-5 pt-5 pb-24 lg:px-8 lg:pt-8 max-w-lg lg:max-w-3xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-5"
      >
        <button
          onClick={() => navigate(from)}
          className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <OutcomeBadge outcome={detail.outcome} decision={detail.decision} submittedAt={detail.submitted_at} />
      </motion.div>

      {/* Patient info */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-4"
      >
        <PatientInfoCard
          info={detail.patient_info}
          reference={detail.ihalaty_reference}
          gmsReferralId={detail.gms_referral_id}
        />
      </motion.div>

      {/* Case timeline */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="mb-4"
      >
        <CaseTimeline
          createdAt={detail.created_at}
          timerExpiresAt={detail.timer_expires_at}
          decidedAt={detail.decided_at}
          submittedAt={detail.submitted_at}
          status={detail.status}
        />
      </motion.div>

      {/* Decision info */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-4"
      >
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
            Decision Details
          </p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            {detail.decided_by_name && (
              <div>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <UserCheck className="w-3.5 h-3.5 text-muted-foreground" />
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Decided By
                  </p>
                </div>
                <p className="text-sm text-foreground font-medium">
                  {detail.decided_by_name}
                </p>
              </div>
            )}
            {detail.decided_at && (
              <div>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Decision Date
                  </p>
                </div>
                <p className="text-sm text-foreground font-medium">
                  {formatDate(detail.decided_at)}
                </p>
              </div>
            )}
            {detail.doctor_name && (
              <div>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Stethoscope className="w-3.5 h-3.5 text-muted-foreground" />
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Doctor
                  </p>
                </div>
                <p className="text-sm text-foreground font-medium">
                  {detail.doctor_name}
                </p>
              </div>
            )}
            {detail.department_name && (
              <div>
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Department
                  </p>
                </div>
                <p className="text-sm text-foreground font-medium">
                  {detail.department_code
                    ? `${detail.department_code} — ${detail.department_name}`
                    : detail.department_name}
                </p>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Attachments */}
      {attachments.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">
            Attachments ({attachments.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {attachments.map((att: Attachment) => {
              const Icon = getFileIcon(att.mime_type);
              const url = api.approvals.getAttachmentUrl(att.id, att.filename);
              return (
                <div
                  key={att.id}
                  className={cn(
                    'group flex items-center gap-2 px-3 py-2 rounded-xl',
                    'border border-border bg-card',
                  )}
                >
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 flex-1 min-w-0 hover:text-primary transition-colors"
                  >
                    <Icon className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate max-w-[180px]">
                        {att.filename}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {att.attachment_type && DOC_TYPE_LABELS[att.attachment_type]
                          ? `${DOC_TYPE_LABELS[att.attachment_type]} · `
                          : ''}
                        {formatFileSize(att.file_size)}
                      </p>
                    </div>
                  </a>
                  <a
                    href={`${url}?download=1`}
                    className="p-1.5 rounded-lg hover:bg-primary-50 transition-colors shrink-0"
                    title={`Download ${att.filename}`}
                  >
                    <Download className="w-3.5 h-3.5 text-muted-foreground hover:text-primary" />
                  </a>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Acceptance Letter */}
      {detail.outcome === 'confirmed' && detail.acceptance_letter && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-4"
        >
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 px-1">
            Acceptance Letter
          </p>
          <div className="flex flex-wrap gap-2">
            {(() => {
              const att = detail.acceptance_letter;
              const url = api.approvals.getAttachmentUrl(att.id, att.filename);
              return (
                <div
                  className={cn(
                    'group flex items-center gap-2 px-3 py-2 rounded-xl',
                    'border border-emerald-200 bg-emerald-50/50',
                  )}
                >
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 flex-1 min-w-0 hover:text-emerald-700 transition-colors"
                  >
                    <FileText className="w-4 h-4 text-emerald-600 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate max-w-[180px]">
                        {att.filename}
                      </p>
                    </div>
                  </a>
                  <a
                    href={`${url}?download=1`}
                    className="p-1.5 rounded-lg hover:bg-emerald-100 transition-colors shrink-0"
                    title={`Download ${att.filename}`}
                  >
                    <Download className="w-3.5 h-3.5 text-emerald-600 hover:text-emerald-700" />
                  </a>
                </div>
              );
            })()}
          </div>
        </motion.div>
      )}
    </div>
  );
}
